import { Link, useNavigate } from 'react-router-dom';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, CreditCard, ShieldCheck, LogOut, FolderOpen, BookOpen, Inbox } from 'lucide-react';
import { authStorage } from '@/utils/authStorage';
import { applyAuthToken } from '@/api/axiosSetup';

function initials(user) {
  const src = `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || user?.username || user?.email || '?';
  return src.split(/[\s@._-]+/).filter(Boolean).slice(0, 2).map((s) => s[0].toUpperCase()).join('');
}

/** Avatar dropdown: profile · plan · data · sign out. */
export default function UserMenu() {
  const navigate = useNavigate();
  const user = authStorage.getAuthorUser();
  if (!user) return null;

  const signOut = () => {
    authStorage.clearAuthorAuth();
    applyAuthToken(null);
    navigate('/thesis/login');
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          data-testid="user-menu-trigger"
          aria-label="Account menu"
          className="w-9 h-9 rounded-full bg-[#0b1a35] text-white text-[11px] font-semibold
            tracking-wide flex items-center justify-center shrink-0 ring-offset-2
            transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-[#1d4ed8]"
        >
          {initials(user)}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-60" data-testid="user-menu-content">
        <DropdownMenuLabel className="pb-1">
          <div className="text-sm font-semibold truncate">
            {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.username}
          </div>
          <div className="text-[11px] font-normal text-muted-foreground truncate">{user.email}</div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild data-testid="user-menu-profile">
          <Link to="/thesis/profile"><User className="w-4 h-4 mr-2" />Profile</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="user-menu-projects">
          <Link to="/thesis"><FolderOpen className="w-4 h-4 mr-2" />My projects</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="user-menu-billing">
          <Link to="/thesis/billing"><CreditCard className="w-4 h-4 mr-2" />Plan &amp; billing</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="user-menu-guide">
          <Link to="/guides"><BookOpen className="w-4 h-4 mr-2" />User guides</Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild data-testid="user-menu-data">
          <Link to="/thesis/account"><ShieldCheck className="w-4 h-4 mr-2" />Data &amp; privacy</Link>
        </DropdownMenuItem>
        {user.is_admin && (
          <DropdownMenuItem asChild data-testid="user-menu-admin">
            <Link to="/thesis/admin/inbox"><Inbox className="w-4 h-4 mr-2" />Admin inbox</Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={signOut} data-testid="user-menu-signout">
          <LogOut className="w-4 h-4 mr-2" />Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
